import { computed, ref } from 'vue';

import { storeToRefs } from 'pinia';

import { useRoute } from 'vue-router/composables';
import { useQuery } from '@/helpers/composables/useApollo';

import {
	GetArticlesTimelineDocument,
	GetArticlesTimelineQuery,
	GetArticlesTimelineQueryVariables,
} from '@/features/editorial/graphql/queries/GetArticleTimeline.query';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { BackdropProfile, ImageFormat } from '@/@types/graphql-types';
import { getHeaderImage } from '@/features/editorial/helpers/editorial-helper';
import { parsePageNumber } from '@/helpers/composables/usePagination';

export const PAGE_SIZE = 30;
export const TIMELINE_MAX_PAGES = 10;

export function useArticleTimeline() {
	const route = useRoute();

	const { language, country, locale } = storeToRefs(useLanguageStore());

	const initialPage = parsePageNumber((route.query.page as string) || '1');

	const queryVariables = computed<GetArticlesTimelineQueryVariables>(() => {
		return {
			input: {
				cursor: (initialPage - 1) * PAGE_SIZE,
				first: PAGE_SIZE,
				locale: locale.value,
			},
			country: country.value,
			language: language.value,
			profile: BackdropProfile.S640,
			format: ImageFormat.Webp,
		};
	});

	const enabled = computed(() => route.name === 'app.editorial.subdomain.home');

	const { result, loading, fetchMore, onError } = useQuery<GetArticlesTimelineQuery>(
		GetArticlesTimelineDocument,
		queryVariables,
		{
			errorPolicy: 'all',
			enabled,
		}
	);

	onError(error => {
		captureMessageForSentry(
			'[GraphQL GetArticleTimeline]:',
			{
				error: error,
				where: 'Component: useArticleTimeline.ts',
			},
			'error'
		);
	});

	const articleTimeline = computed(() =>
		result.value?.getArticlesTimeline?.articleGroups?.map(bucket => ({
			...bucket,
			articles: bucket.articles.map(article => ({ ...article, headerImage: getHeaderImage(article) })),
		}))
	);

	const nextCursor = computed(() => result.value?.getArticlesTimeline?.nextCursor ?? 0);
	const hasMore = computed(() => nextCursor.value > 0);
	const totalBucketCount = computed(() => result.value?.getArticlesTimeline?.totalAvailableBucketCount ?? 0);
	const lastPage = computed(() =>
		totalBucketCount.value ? Math.ceil(totalBucketCount.value / PAGE_SIZE) : TIMELINE_MAX_PAGES
	);

	const startPage = ref(initialPage);
	const hasPrev = computed(() => startPage.value > 1);

	async function fetchPrevArticles() {
		if (!hasPrev.value) return false;

		const prevPage = startPage.value - 1;
		const prevCursor = (prevPage - 1) * PAGE_SIZE;

		await fetchMore({
			variables: {
				input: {
					cursor: prevCursor,
					first: PAGE_SIZE,
					locale: locale.value,
				},
			},
			updateQuery: (previousResult, { fetchMoreResult }) => {
				if (!fetchMoreResult) return previousResult;

				return {
					...previousResult,
					getArticlesTimeline: {
						...previousResult.getArticlesTimeline,
						articleGroups: [
							...fetchMoreResult.getArticlesTimeline.articleGroups,
							...previousResult.getArticlesTimeline.articleGroups,
						],
					},
				};
			},
		});

		startPage.value = prevPage;

		return true;
	}

	async function fetchMoreArticles() {
		if (!hasMore.value) return false;

		await fetchMore({
			variables: {
				input: {
					cursor: nextCursor.value,
					first: PAGE_SIZE,
					locale: locale.value,
				},
			},
			updateQuery: (previousResult, { fetchMoreResult }) => {
				if (!fetchMoreResult) return previousResult;

				return {
					...fetchMoreResult,
					getArticlesTimeline: {
						...fetchMoreResult.getArticlesTimeline,
						articleGroups: [
							...previousResult.getArticlesTimeline.articleGroups,
							...fetchMoreResult.getArticlesTimeline.articleGroups,
						],
					},
				};
			},
		});

		return true;
	}

	return {
		articleTimeline,
		loading,
		totalBucketCount,
		lastPage,
		fetchMoreArticles,
		fetchPrevArticles,
	};
}
